import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
} from "react";
import { Container } from "@pixi/react";
import HoldAndWinTensionOverlayController from "./HoldAndWinTensionOverlayController";
import { safeInit } from "../../utilities";

/**
 * Hold and Win Tension Overlay Component
 * Manages tension animation for a single symbol position
 * Completely independent from multiplier animations
 */
const HoldAndWinTensionOverlay = forwardRef(
  ({ reelIndex, symbolIndex, symbolWidth, symbolHeight }, ref) => {
    const tensionControllerRef = useRef(null);
    const tensionContainerRef = useRef(null);

    // Initialize controller when assets are ready
    useEffect(() => {
      if (tensionContainerRef.current && !tensionControllerRef.current) {
        safeInit(() => {
          tensionControllerRef.current = new HoldAndWinTensionOverlayController(
            tensionContainerRef.current,
            reelIndex,
            symbolIndex,
            symbolWidth,
            symbolHeight
          );
        });
      }

      return () => {
        if (tensionControllerRef.current) {
          tensionControllerRef.current.cleanup?.();
          tensionControllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle symbol size changes for responsive design
    useEffect(() => {
      if (tensionControllerRef.current) {
        tensionControllerRef.current.resizeTension(symbolWidth, symbolHeight);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbolHeight]);

    // Expose controller methods to parent components
    useImperativeHandle(ref, () => ({
      playTensionIn: () => {
        tensionControllerRef.current?.playTensionIn();
      },

      playTensionOut: () => {
        tensionControllerRef.current?.playTensionOut();
      },

      hideTension: () => {
        tensionControllerRef.current?.hideTension();
      },

      forceStopTension: () => {
        tensionControllerRef.current?.forceStopTension();
      },
    }));

    return (
      <Container
        ref={tensionContainerRef}
        name={`hold_and_win_tension_${reelIndex}_${symbolIndex}`}
        sortableChildren
      />
    );
  }
);

export default HoldAndWinTensionOverlay;
